import React, { useMemo } from "react";
import * as echarts from "echarts";
import ScreeningPolicyCard from "./ScreeningPolicyCard";

/* ---------- tiny echarts card ---------- */

function useEchart(option) {
  const ref = React.useRef(null);
  const chartRef = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // init once (no theme applied - keep default gray background)
    if (!chartRef.current) {
      chartRef.current = echarts.init(el, undefined, { renderer: "canvas" });
    }

    // apply option
    if (option)
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });

    // ensure first paint matches container size
    requestAnimationFrame(() => chartRef.current?.resize());

    // resize on window + container size changes
    const handle = () => chartRef.current?.resize();
    window.addEventListener("resize", handle);
    const ro = new ResizeObserver(handle);
    ro.observe(el);

    return () => {
      window.removeEventListener("resize", handle);
      ro.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, []); // init

  React.useEffect(() => {
    if (chartRef.current && option) {
      chartRef.current.setOption(option, { notMerge: true, lazyUpdate: true });
      requestAnimationFrame(() => chartRef.current?.resize());
    }
  }, [option]);

  return ref;
}

function ChartCard({ option }) {
  const ref = useEchart(option);
  return (
    <div className="chart-card">
      <div ref={ref} style={{ width: "100%", height: "100%" }} />
    </div>
  );
}

/* ---------- dataset IDs you provided ---------- */
const UMN_ID = "68c95f772e406f5386ab6598"; // Unmet Medical Needs
const AGE_ID = "68c95f77de2927233dcc520e"; // Age distribution
const IMM_ID = "68c95f77f9a46539625d96a8"; // Immigration
const AROPE_ID = "68c95f77d20f49bd5060a378"; // AROPE NUTS-2

/* ---------- helpers ---------- */
function applyTextColor(option) {
  if (!option) return option;
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const textColor = isDark ? '#e0e0e0' : '#333';

  return {
    ...option,
    title: option.title ? { ...option.title, textStyle: { ...option.title.textStyle, color: textColor } } : undefined,
    legend: option.legend ? { ...option.legend, textStyle: { ...option.legend.textStyle, color: textColor } } : undefined,
    xAxis: option.xAxis ? (Array.isArray(option.xAxis)
      ? option.xAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.xAxis, axisLabel: { ...option.xAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.xAxis.nameTextStyle, color: textColor } })
      : undefined,
    yAxis: option.yAxis ? (Array.isArray(option.yAxis)
      ? option.yAxis.map(axis => ({ ...axis, axisLabel: { ...axis.axisLabel, color: textColor }, nameTextStyle: { ...axis.nameTextStyle, color: textColor } }))
      : { ...option.yAxis, axisLabel: { ...option.yAxis.axisLabel, color: textColor }, nameTextStyle: { ...option.yAxis.nameTextStyle, color: textColor } })
      : undefined,
  };
}

const getEls = (dataById, id) => dataById?.[id]?.elements || [];
const num = (v) => {
  const n = Number(
    String(v ?? "")
      .replaceAll(",", "")
      .trim()
  );
  return Number.isFinite(n) ? n : null;
};
const yearColsUnion = (rows) =>
  Array.from(
    new Set(
      rows.flatMap((r) =>
        Object.keys(r || {}).filter((k) => /^\d{4}$/.test(String(k)))
      )
    )
  ).sort();

/* ======================
   UNMET MEDICAL NEEDS
   ====================== */
function unmetByReason(elements, reason, title) {
  const rows = elements.filter(
    (r) => r["Demo Group Type"] === "NUTS-2" && r["Reason"] === reason
  );
  if (!rows.length) return null;
  const years = yearColsUnion(rows); // 2018..2023

  const series = rows.map((r) => ({
    name: r["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(r[y])),
  }));

  return {
    title: {
      text: title,
      left: "center",
      width: "90%",
      textStyle: {
        overflow: "break",
        lineHeight: 20
      }
    },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 48 },
    grid: { left: 56, right: 24, top: 92, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

/* ======================
   AGE PYRAMID (Spain)
   ====================== */
function agePyramidSpain(elements) {
  const rows = elements.filter((r) => r["Demographic Group"] === "Total Spain");
  if (!rows.length) return null;

  const ages = rows.map((r) => String(r["Age Group"]));
  const yCats = [...ages].reverse();
  // In this dataset Men are already negative; show as-is (women positive)
  const men = rows.map((r) => num(r["Men"])).reverse();
  const women = rows.map((r) => num(r["Women"])).reverse();

  return {
    title: {
      text: "Age Pyramid Spain on 01/01/2024",
      left: "center",
      width: "90%",
      textStyle: {
        overflow: "break",
        lineHeight: 20
      }
    },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 88, right: 48, top: 72, bottom: 24 },
    xAxis: [
      {
        type: "value",
        axisLabel: { formatter: (v) => Math.abs(v).toLocaleString() },
      },
    ],
    yAxis: [{ type: "category", data: yCats }],
    series: [
      { name: "Men", type: "bar", stack: "pop", data: men },
      { name: "Women", type: "bar", stack: "pop", data: women },
    ],
  };
}

/* ======================
   AROPE (NUTS-2)
   ====================== */
function aropeByNUTS2(elements) {
  const rows = elements.filter((r) => r["Demo Group Type"] === "NUTS-2");
  if (!rows.length) return null;
  const years = yearColsUnion(rows); // 2019..2023

  const series = rows.map((r) => ({
    name: r["Demographic Group"],
    type: "line",
    smooth: true,
    data: years.map((y) => num(r[y])),
  }));

  return {
    title: {
      text: "AROPE by NUTS-2 Region",
      left: "center",
      width: "90%",
      textStyle: {
        overflow: "break",
        lineHeight: 20
      }
    },
    tooltip: { trigger: "axis" },
    legend: { type: "scroll", top: 28 },
    grid: { left: 56, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: years },
    yAxis: { type: "value", name: "%" },
    series,
  };
}

/* ======================
   Immigration (bars)
   ====================== */
function immigration2020(elements) {
  const rows = elements.filter(
    (r) =>
      r["Demo Group Type"] === "Country of Birth" &&
      ["EU", "Non-EU Europe", "Non-Europe"].includes(
        String(r["Demographic Group"])
      )
  );
  if (!rows.length) return null;

  const x = rows.map((r) => String(r["Demographic Group"]));
  const women = rows.map((r) => num(r["Women"]));
  const men = rows.map((r) => num(r["Men"]));
  const both = rows.map((r) => num(r["Both"]));

  return {
    title: {
      text: "Total No. of Immigrants by Region 2020",
      left: "center",
      width: "90%",
      textStyle: {
        overflow: "break",
        lineHeight: 20
      }
    },
    tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
    legend: { top: 28 },
    grid: { left: 64, right: 24, top: 72, bottom: 40 },
    xAxis: { type: "category", data: x },
    yAxis: { type: "value" },
    series: [
      { name: "Women", type: "bar", data: women },
      { name: "Men", type: "bar", data: men },
      { name: "Both", type: "bar", data: both },
    ],
  };
}

/* ======================
   Main component
   ====================== */
export default function SpainDashboard({ dataById, policyData }) {
  const charts = useMemo(() => {
    const out = [];

    // Unmet Medical Needs: 3 charts
    const umn = getEls(dataById, UMN_ID);
    if (umn.length) {
      const socio = applyTextColor(unmetByReason(
        umn,
        "Set 1",
        "% with Unmet Medical Needs for Socio-Economic Reasons\nby NUTS-2 Region"
      ));
      const personal = applyTextColor(unmetByReason(
        umn,
        "Set 2",
        "% with Unmet Medical Needs for Personal Reasons\nby NUTS-2 Region"
      ));
      const any = applyTextColor(unmetByReason(
        umn,
        "Any",
        "% with Unmet Medical Needs for Any Reason\nby NUTS-2 Region"
      ));
      if (socio) out.push({ key: "es-umn-socio", option: socio });
      if (personal) out.push({ key: "es-umn-personal", option: personal });
      if (any) out.push({ key: "es-umn-any", option: any });
    }

    // Age pyramid
    const age = getEls(dataById, AGE_ID);
    if (age.length) {
      const c = applyTextColor(agePyramidSpain(age));
      if (c) out.push({ key: "es-age", option: c });
    }

    // AROPE by NUTS-2
    const arope = getEls(dataById, AROPE_ID);
    if (arope.length) {
      const c = applyTextColor(aropeByNUTS2(arope));
      if (c) out.push({ key: "es-arope", option: c });
    }

    // Immigration
    const imm = getEls(dataById, IMM_ID);
    if (imm.length) {
      const c = applyTextColor(immigration2020(imm));
      if (c) out.push({ key: "es-imm", option: c });
    }

    return out;
  }, [dataById]);

  if (!charts.length && !policyData) return null;

  return (
    <div className="chart-grid">
      {policyData && <ScreeningPolicyCard policyData={policyData} />}
      {charts.map((c) => (
        <ChartCard key={c.key} option={c.option} />
      ))}
    </div>
  );
}
